import { gql } from "@apollo/client";
import { CurrencyCode } from "@asmbl/shared/constants";
import { ratio } from "@asmbl/shared/money";
import { formatNumeral } from "@asmbl/shared/utils";
import {
  CashCompType,
  CondensedTableActualRecurringBonusPercentCell2_participant as Participant,
} from "../../../../__generated__/graphql";
import { AssembleTruncatedTypography } from "../../../../components/AssembleTruncatedTypography";
import { useCurrencies } from "../../../../components/CurrenciesContext";
import { activeComp, cashValueOf } from "../../../../models/CashCompensation";
import { getActualRecurringBonus } from "../../../../models/CompRecommendation";
import { ColumnComponent2 } from "../CondensedTable/CondensedTableInner";
import { ColumnIds, ColumnIdsToHeaders } from "../Contexts/ColumnOrderContext";
import { LARGE_COL_WIDTH } from "./dimensions";

type Props = {
  row: { original: Participant };
};

function getPercentOfTarget(
  employee: Participant,
  defaultCurrencyCode: CurrencyCode
) {
  const activeCashComp = activeComp(
    employee.subject.activeCashCompensation ?? [],
    new Date()
  );
  const target = cashValueOf(CashCompType.RECURRING_BONUS, activeCashComp);
  const bonus = getActualRecurringBonus(
    employee.compRecommendation?.latestSubmittedItems ?? [],
    activeCashComp.get(CashCompType.SALARY) ?? null,
    defaultCurrencyCode
  );
  if (bonus.value === 0) {
    return 0;
  }
  // prevent divide by zero errors
  if (target?.value === 0) {
    return null;
  }

  if (target && target.currency === bonus.currency) {
    return ratio(bonus, target);
  }
  return null;
}

export function CondensedTableActualRecurringBonusPercentCell2({
  row: { original: employee },
}: Props): JSX.Element {
  const { defaultCurrencyCode } = useCurrencies();
  const percent = getPercentOfTarget(employee, defaultCurrencyCode);
  return (
    <AssembleTruncatedTypography align="right">
      {percent === null
        ? "N/A"
        : formatNumeral(percent, {
            style: "percent",
            maximumFractionDigits: 2,
          })}
    </AssembleTruncatedTypography>
  );
}

CondensedTableActualRecurringBonusPercentCell2.fragments = {
  participant: gql`
    fragment CondensedTableActualRecurringBonusPercentCell2_participant on CompCycleParticipant {
      compRecommendation(skipEligibility: $skipEligibility) {
        subjectId
        compCycleId
        latestSubmittedItems {
          id
          recommendationType
          recommendedCashValue(currencyCode: $currencyCode)
        }
      }
      subject {
        activeCashCompensation(currencyCode: $currencyCode) {
          employeeId
          type
          activeAt
          unit
          annualCashEquivalent
          hourlyCashEquivalent
          unit
          percentOfSalary
        }
      }
    }
  `,
};

CondensedTableActualRecurringBonusPercentCell2.Header = ColumnIdsToHeaders.get(
  ColumnIds.ACTUAL_RECURRING_BONUS_PERCENT
);
CondensedTableActualRecurringBonusPercentCell2.id =
  ColumnIds.ACTUAL_RECURRING_BONUS_PERCENT;

const column: ColumnComponent2["column"] = {
  Cell: CondensedTableActualRecurringBonusPercentCell2,
  Header: CondensedTableActualRecurringBonusPercentCell2.Header,
  id: CondensedTableActualRecurringBonusPercentCell2.id,
  width: LARGE_COL_WIDTH,
};
CondensedTableActualRecurringBonusPercentCell2.column = column;
